import React, { useState, useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';
import { useWebSocketChat } from '../hooks/useWebSocketChat';
import { ConnectionStatus } from '../types/chat';

interface ChatPanelProps {
  environmentId: string;
  environmentName?: string;
  onClose: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  environmentId,
  environmentName,
  onClose
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const {
    messages,
    connectionStatus,
    sendMessage,
    loadMoreMessages,
    currentSessionId,
    isSessionRestored,
    hasMore
  } = useWebSocketChat(environmentId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim() && connectionStatus === 'connected') {
      // Include basic environment context as metadata
      const metadata = {
        currentFile: environmentName,
        projectContext: { environmentId }
      };
      
      sendMessage(inputMessage.trim(), metadata);
      setInputMessage('');
    }
  };

  const getStatusText = (status: ConnectionStatus) => {
    switch (status) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Connected';
      case 'disconnected': return 'Disconnected';
      case 'error': return 'Connection Error';
      default: return status;
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e0e0e0', background: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {environmentName && (
              <div style={{ fontSize: '14px', color: '#666' }}>
                Environment: {environmentName}
              </div>
            )}
            {isSessionRestored && currentSessionId && (
              <div style={{ fontSize: '12px', color: '#28a745', marginTop: '4px' }}>
                Session resumed • {messages.length} messages
              </div>
            )}
          </div>
          <span className={`status-indicator ${connectionStatus}`}>
            {getStatusText(connectionStatus)}
          </span>
        </div>
      </div>
      
      <div className="chat-messages" style={{ overflow: 'auto', padding: '16px', minHeight: 0 }}>
        {hasMore && messages.length > 0 && (
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <button
              onClick={loadMoreMessages}
              disabled={connectionStatus !== 'connected'}
              style={{
                padding: '8px 16px',
                background: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#666'
              }}
            >
              Load More Messages
            </button>
          </div>
        )}
        {messages.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            color: '#666', 
            padding: '40px 20px',
            fontStyle: 'italic'
          }}>
            Start a conversation with the AI assistant about this environment
          </div>
        )}
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      <div style={{ padding: '16px', borderTop: '1px solid #e0e0e0', background: 'white' }}>
        <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask me anything about this environment..."
            disabled={connectionStatus !== 'connected'}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #ddd',
              borderRadius: '24px',
              outline: 'none',
              fontSize: '14px'
            }}
          />
          <button
            type="submit"
            disabled={!inputMessage.trim() || connectionStatus !== 'connected'}
            style={{
              padding: '12px 20px',
              background: connectionStatus === 'connected' ? '#1976d2' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: connectionStatus === 'connected' ? 'pointer' : 'not-allowed',
              fontWeight: '500'
            }}
          >
            Send
          </button>
        </form>
        {currentSessionId && (
          <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '8px' }}>
            Session: {currentSessionId.substring(0, 8)}...
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPanel;